import React from 'react';
import { TESTIDS } from '@/lib/testIds';
import { Bee, Sprig, Gladiolus } from './Illustrations';

const BOOK_COVER = '/images/book-cover.jpg';
const BOOKBABY_URL = 'https://store.bookbaby.com/book/the-feelings-bench';

const themes = [
  'Emotional Literacy',
  'Self-Awareness',
  'Kindness',
  'Belonging',
  'Social-Emotional Learning',
  'Mental Wellness',
];

const praise = [
  {
    quote:
      "A beautiful reminder that every feeling deserves a safe space. This is a book I'll return to again and again, both at home and in the classroom.",
    author: 'Parent & Educator',
  },
  {
    quote:
      'I liked all the different feelings because sometimes I feel those too. My favorite part was when everyone had a place on the bench.',
    author: 'Early Reader, Age 7',
  },
];

export default function FeaturedBook() {
  return (
    <section
      id="book"
      data-testid={TESTIDS.book.section}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* washy background band */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7F2] via-[#FBF7F2] to-[#FBE8EE]/40" />
      <Sprig className="absolute left-6 top-16 w-48 opacity-60" />
      <Gladiolus className="absolute right-2 bottom-6 w-40 opacity-70 hidden md:block" flip />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="text-center max-w-2xl mx-auto">
          <p className="font-accent text-2xl text-[#8D68B2]">The Debut</p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#24385C] mt-2 leading-tight">
            The <span className="squiggle-underline">Feelings</span> Bench
          </h2>
          <p className="mt-6 font-body text-lg text-[#24385C]/80">
            A gentle picture book that invites every emotion to take a seat.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-14 items-center">
          {/* Book cover */}
          <div className="lg:col-span-2 relative">
            <div className="absolute inset-0 -m-8 bg-[#A8B78C]/25 blob-shape-1" />
            <div className="absolute inset-0 -m-4 bg-[#F5C857]/25 blob-shape-3" />
            <div className="relative bg-white rounded-[28px] p-4 shadow-[0_30px_60px_-20px_rgba(36,56,92,0.4)] rotate-2 hover:rotate-0 transition-transform duration-500">
              <img
                src={BOOK_COVER}
                alt="The Feelings Bench book cover"
                className="w-full h-auto rounded-2xl block"
                loading="lazy"
              />
            </div>
            <Bee className="absolute -top-8 -left-6 w-20 animate-float-tiny" />
          </div>

          {/* Description + actions */}
          <div className="lg:col-span-3">
            <div className="bg-white/80 backdrop-blur border-2 border-white rounded-[28px] p-8 md:p-10 shadow-[0_20px_50px_-25px_rgba(36,56,92,0.25)]">
              <p className="font-accent text-3xl text-[#8D68B2] leading-tight">
                Big feelings don&rsquo;t have to be hidden,
                hurried away, or feared.
              </p>
              <p className="font-accent text-3xl text-[#B88ACB] leading-tight mt-1">
                Sometimes, they simply need a place to sit.
              </p>

              <div className="mt-6 space-y-4 font-body text-[17px] text-[#24385C]/85 leading-relaxed">
                <p>
                  In <em>The Feelings Bench</em>, emotions come to life as colorful visitors, each
                  bringing its own thoughts, sensations, and perspective. Through gentle storytelling
                  and engaging illustrations, children are invited to notice their feelings, listen
                  to what they have to say, and welcome every emotion with curiosity, compassion,
                  and care.
                </p>
                <p>
                  Perfect for families, classrooms, counselors, and caregivers,
                  <em> The Feelings Bench</em> encourages emotional awareness, resilience,
                  self-acceptance, and meaningful conversations about mental and emotional well-being.
                </p>
                <p>
                  A comforting reminder for readers of all ages that every feeling belongs&mdash;and
                  that no one has to face their feelings alone.
                </p>
              </div>

              <div className="mt-8">
                <p className="font-body text-sm uppercase tracking-widest text-[#8D68B2] font-semibold">
                  Themes
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {themes.map((t) => (
                    <span
                      key={t}
                      className="text-sm font-body font-semibold text-[#24385C] bg-[#FBE8EE] px-4 py-1.5 rounded-full border border-white"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  data-testid={TESTIDS.book.buyBookbaby}
                  href={BOOKBABY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#8D68B2] hover:bg-[#24385C] text-white rounded-full px-6 py-3 font-body font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                >
                  Buy on BookBaby
                </a>
                <a
                  data-testid={TESTIDS.book.buyAmazon}
                  href="https://www.amazon.com/Feelings-Bench-Sitting-Your-Emotions/dp/B0H8X4LMS1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#F5C857] hover:bg-[#F1BE3E] text-[#24385C] rounded-full px-6 py-3 font-body font-semibold shadow-md transition-all hover:-translate-y-0.5"
                >
                  Buy on Amazon
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Praise */}
        <div className="mt-20">
          <div className="text-center">
            <p className="font-accent text-2xl text-[#8D68B2]">Early Praise</p>
            <h3 className="font-heading text-3xl md:text-4xl text-[#24385C] mt-2">Kind words</h3>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {praise.map((p, i) => (
              <div
                key={i}
                className="relative bg-white rounded-[28px] p-8 pb-12 border-2 border-white shadow-[0_10px_30px_-10px_rgba(36,56,92,0.15)]"
              >
                <span className="absolute -top-4 left-6 font-heading text-6xl text-[#F5C857] leading-none">“</span>
                <p className="font-accent text-2xl text-[#24385C] leading-snug">{p.quote}</p>
                <span className="absolute -bottom-8 right-6 font-heading text-6xl text-[#F5C857] leading-none">”</span>
                <p className="mt-4 font-body text-sm text-[#8D68B2] font-semibold uppercase tracking-wider">— {p.author}</p>
              </div>
            ))}
            <div className="md:col-span-2 text-center font-body text-sm text-[#24385C]/60">
              More reviews coming soon — your kind words are always welcome.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
